// InvoicePaymentDialog.jsx — v4
// Changes from v3:
//   · resolveRecipientGUID FIXED: uses exact FK lookups (getByUserId / getByCreatorUsername)
//     instead of fuzzy search. Old search ?q=541 matched "609541500@qq.com" (contains "541")
//     and credited the wrong GlobalUser. Exact lookup hits global_users.user_id = 541 directly.
//   · API.globalUsers.getByUserId and getByCreatorUsername must be present in API/index.js
//     (endpoints: GLOBAL_USER_BY_USER_ID, GLOBAL_USER_BY_CREATOR_USERNAME in endpoints.js)

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  Stack, Chip, CircularProgress, Slide, useMediaQuery, IconButton,
  Paper, Divider, Collapse, Alert,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  East as ArrowIcon,
  TrendingFlat as TrendingFlatIcon,
  Close as CloseIcon,
  Payment as PayPalIcon,
  OpenInNew as RedirectIcon,
  Lock as LockIcon,
  WarningAmber as WarnIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';
import API from '../../../API';

// ─── T2A fee math ─────────────────────────────────────────────────────────────
const RAILS_FEE = {
  paypal: { pct: 0.029, fixed: 0.30 },
  stripe: { pct: 0.035, fixed: 0.30 },
};
function computeT2AFee(amount, rail = 'paypal') {
  const r = RAILS_FEE[rail] || RAILS_FEE.paypal;
  const gross = (amount + r.fixed) / (1 - r.pct);
  return {
    senderPays:        +gross.toFixed(2),
    recipientReceives: +amount.toFixed(2),
    fee:               +(gross - amount).toFixed(2),
  };
}

// ─── Recipient resolution — FIXED v4 ─────────────────────────────────────────
// Uses exact FK lookups. NEVER use fuzzy search (?q=<user_id>) here.
//
// WHY THE OLD VERSION BROKE:
//   Old code: API.globalUsers.search({ q: String(invoice.user_id) })
//   invoice.user_id = 541 → search ?q=541 → backend does ilike('%541%') on emails
//   GU 1694 has email "609541500@qq.com" which contains "541" → wrong match
//   GU 1288 (thealexroa, user_id=541) → correct but lost the race
//
// invoice.creator_id  = username string → exact match on creator_username FK
// invoice.user_id     = integer         → exact match on user_id FK
// invoice.client_email = the PAYER      → do NOT use, it's the wrong person
async function resolveRecipientGUID(invoice) {
  if (!invoice) return null;

  try {
    // Creator invoice — creator_id is the username string
    if (invoice.creator_id) {
      const res = await API.globalUsers.getByCreatorUsername(invoice.creator_id);
      const id = res?.id ?? res?.global_user?.id ?? null;
      if (id) return id;
    }

    // User invoice — user_id is an exact integer FK on global_users.user_id
    if (invoice.user_id) {
      const res = await API.globalUsers.getByUserId(invoice.user_id);
      const id = res?.id ?? res?.global_user?.id ?? null;
      if (id) return id;
    }
  } catch (e) {
    console.warn('[InvoicePaymentDialog] resolveRecipientGUID failed:', e?.response?.data || e?.message);
  }

  return null;
}

export default function InvoicePaymentDialog({ open, onClose, invoice, onSuccess }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [payMethod, setPayMethod]         = useState('balance');
  const [t2aRail,   setT2aRail]           = useState('paypal');
  const [t2aOpen,   setT2aOpen]           = useState(false);
  const [busy,      setBusy]              = useState(false);
  const [recipientGuid, setRecipientGuid] = useState(null);
  const [guidState, setGuidState]         = useState('idle'); // 'idle'|'loading'|'ok'|'fail'

  const idempotencyKey = useRef(
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  );

  // ── Resolve recipient on open ─────────────────────────────────────────────
  useEffect(() => {
    if (!open || !invoice) return;
    setGuidState('loading');
    resolveRecipientGUID(invoice).then((id) => {
      setRecipientGuid(id);
      setGuidState(id ? 'ok' : 'fail');
    });
  }, [open, invoice?.id]);

  // ── Data queries ──────────────────────────────────────────────────────────
  const { data: balData,   isLoading: balLoading }  = useQuery(['myBalance'],      () => API.balanceLedger.fetchMyBalance(),  { enabled: open });
  const { data: spendData, isLoading: spendLoading } = useQuery(['mySpendingPower'], () => API.teams.getMySpendingInfo(),       { enabled: open });

  const senderId    = balData?.user?.id ?? null;
  const invoiceAmt  = parseFloat(invoice?.amount_due || 0);
  const spendFee    = invoiceAmt * 0.05;
  const totalCredit = invoiceAmt + spendFee;
  const balAvail    = parseFloat(balData?.balance || 0);
  const spendAvail  = parseFloat(spendData?.total_available_funds || 0);
  const canBalance  = balAvail  >= invoiceAmt;
  const canSpend    = spendAvail >= totalCredit;
  const t2aCalc     = computeT2AFee(invoiceAmt, t2aRail);
  const isLoading   = balLoading || spendLoading;

  // Auto-select
  useEffect(() => {
    if (isLoading) return;
    if (canBalance)  { setPayMethod('balance'); setT2aOpen(false); return; }
    if (canSpend)    { setPayMethod('credit');  return; }
    setPayMethod('balance'); setT2aOpen(true);
  }, [isLoading, canBalance, canSpend]);

  // ─── T2A mutation ─────────────────────────────────────────────────────────
  const t2aMutation = useMutation(
    async () => {
      if (!senderId)      throw new Error('Sender ID not loaded — refresh and try again');
      if (!recipientGuid) throw new Error('Could not verify recipient — try again shortly');
      return API.balanceLedger.t2aGift({
        sender_id:    senderId,
        description:  invoice?.notes || invoice?.client_notes || `Invoice #${invoice?.po_number}`,
        payment_rail: t2aRail,
        fee_payer:    'sender',
        transfers: [{
          type:                     'internal',
          amount:                   String(t2aCalc.senderPays),
          recipient_global_user_id: recipientGuid,
          metadata: {
            t2a:               true,
            agreed_amount:     invoiceAmt,
            fee_amount:        t2aCalc.fee,
            fee_payer:         'sender',
            payment_rail:      t2aRail,
            invoice_id:        invoice?.id,
            invoice_po_number: invoice?.po_number,
          },
        }],
      });
    },
    {
      onSuccess: (data) => {
        const url = data?.approval_url || data?.url;
        if (url) { window.location.href = url; }
        else { onSuccess?.(data); onClose?.(); }
      },
      onError: (err) => {
        alert(err?.response?.data?.error || err?.message || 'Payment initiation failed — please try again.');
      },
    }
  );

  // ─── Handle pay ───────────────────────────────────────────────────────────
  const handlePay = async () => {
    if (t2aOpen && payMethod === 'balance') {
      t2aMutation.mutate();
      return;
    }
    setBusy(true);
    try {
      let res;
      if (payMethod === 'balance') {
        res = await API.invoices.payWithBalance(invoice.id, { amount: invoiceAmt, idempotency_key: idempotencyKey.current });
      } else {
        res = await API.invoices.payWithCredit(invoice.id, { amount: invoiceAmt, idempotency_key: idempotencyKey.current });
      }
      onSuccess?.(res);
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.error || 'Payment failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const isBusy = busy || t2aMutation.isLoading;

  const canProceed = (() => {
    if (isBusy) return false;
    if (payMethod === 'credit') return canSpend;
    if (!t2aOpen) return canBalance;
    return true;
  })();

  const btnLabel = (() => {
    if (isBusy) return 'Processing…';
    if (payMethod === 'credit') return `Pay with Spending Power · $${totalCredit.toFixed(2)}`;
    if (t2aOpen) return `Pay ${t2aRail === 'paypal' ? 'via PayPal' : 'via Card'} · $${t2aCalc.senderPays.toFixed(2)}`;
    return `Confirm Payment · $${invoiceAmt.toFixed(2)}`;
  })();

  const safeTop        = 'env(safe-area-inset-top, 0px)';
  const safeBottom     = 'env(safe-area-inset-bottom, 0px)';
  const appShellHeight = 86;

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' } }}>
        <DialogContent sx={{ textAlign: 'center', py: isMobile ? 12 : 8 }}>
          <CircularProgress sx={{ color: 'white' }} size={48} />
          <Typography sx={{ mt: 3, color: 'white', fontWeight: 600 }}>Loading payment options…</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Balance card ─────────────────────────────────────────────────────────
  const BalanceCard = () => {
    const isSelected  = payMethod === 'balance';
    const t2aExpanded = isSelected && t2aOpen;

    return (
      <Box onClick={() => { if (!isSelected) setPayMethod('balance'); }}
        sx={{
          borderRadius: isMobile ? 2.5 : 3,
          border: '2px solid',
          borderColor: isSelected ? 'transparent' : alpha(theme.palette.grey[300], 0.5),
          background: isSelected ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : alpha(theme.palette.grey[100], 0.5),
          cursor: isSelected ? 'default' : 'pointer',
          transition: 'all 0.25s ease',
          transform: isSelected ? 'scale(1.012)' : 'scale(1)',
          boxShadow: isSelected ? '0 12px 28px rgba(102,126,234,0.3)' : '0 2px 8px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}>

        <Box sx={{ p: isMobile ? 2 : 2.5 }}>
          <Stack spacing={1.75}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2,
                bgcolor: isSelected ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.primary.main, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <WalletIcon sx={{ fontSize: 20, color: isSelected ? 'white' : theme.palette.primary.main }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.2 }}>
                  <Typography variant="subtitle2" fontWeight={700}
                    sx={{ color: isSelected ? 'white' : 'text.primary', fontSize: '0.95rem' }}>
                    Blitz Balance
                  </Typography>
                  <Chip label="No Fees" size="small" sx={{
                    height: 17, fontSize: 10, fontWeight: 700,
                    bgcolor: isSelected ? 'rgba(255,255,255,0.22)' : alpha(theme.palette.success.main, 0.1),
                    color: isSelected ? 'white' : theme.palette.success.dark,
                  }} />
                  {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: 'white', ml: 'auto' }} />}
                </Stack>
                <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.78)' : 'text.secondary', fontSize: 11 }}>
                  Instant · zero fees
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center"
              sx={{ py: 1.25, px: 1.5, borderRadius: 2,
                bgcolor: isSelected ? 'rgba(255,255,255,0.13)' : alpha(theme.palette.grey[100], 0.8) }}>
              <Box>
                <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.62)' : 'text.secondary', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Available</Typography>
                <Typography variant="subtitle1" fontWeight={700}
                  sx={{ color: isSelected ? 'white' : (canBalance ? theme.palette.success.main : theme.palette.error.main), fontSize: '1.05rem' }}>
                  ${balAvail.toFixed(2)}
                </Typography>
              </Box>
              <TrendingFlatIcon sx={{ color: isSelected ? 'rgba(255,255,255,0.4)' : 'text.disabled', fontSize: 18 }} />
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.62)' : 'text.secondary', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>After</Typography>
                <Typography variant="subtitle1" fontWeight={700}
                  sx={{ color: isSelected ? 'white' : 'text.primary', fontSize: '1.05rem' }}>
                  ${(balAvail - invoiceAmt).toFixed(2)}
                </Typography>
              </Box>
            </Stack>

            {isSelected && !canBalance && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <WarnIcon sx={{ fontSize: 13, color: 'rgba(255,220,100,0.9)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,220,100,0.9)', fontSize: 11, fontWeight: 600 }}>
                    Need ${Math.max(0, invoiceAmt - balAvail).toFixed(2)} more
                  </Typography>
                </Stack>
                <Chip
                  label={t2aOpen ? 'Use balance instead ↑' : 'Pay with card/PayPal →'}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setT2aOpen((v) => !v); }}
                  sx={{
                    height: 22, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                    bgcolor: t2aOpen ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.18)',
                    color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                  }} />
              </Stack>
            )}

            {isSelected && canBalance && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="caption"
                  onClick={(e) => { e.stopPropagation(); setT2aOpen((v) => !v); }}
                  sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10.5, cursor: 'pointer', textDecoration: 'underline',
                    '&:hover': { color: 'rgba(255,255,255,0.9)' } }}>
                  {t2aOpen ? 'Cancel — use balance' : 'Pay with card instead'}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Collapse in={isSelected && t2aOpen} unmountOnExit>
          <Box sx={{ px: isMobile ? 2 : 2.5, pb: isMobile ? 2 : 2.5 }}>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mb: 2 }} />

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
              Choose method
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {[
                { id: 'paypal', label: 'PayPal',    icon: <PayPalIcon sx={{ fontSize: 15 }} /> },
                { id: 'stripe', label: 'Card/Bank', icon: <CreditCardIcon sx={{ fontSize: 15 }} /> },
              ].map((r) => (
                <Box key={r.id}
                  onClick={(e) => { e.stopPropagation(); setT2aRail(r.id); }}
                  sx={{ flex: 1, py: 1, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                    bgcolor: t2aRail === r.id ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.1)',
                    border: '1.5px solid', borderColor: t2aRail === r.id ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.2)',
                    transition: 'all 0.18s', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.6}>
                    <Box sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>{r.icon}</Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: 'white', fontSize: 11 }}>{r.label}</Typography>
                    {t2aRail === r.id && <CheckCircleIcon sx={{ fontSize: 12, color: 'white' }} />}
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Stack spacing={0.65} sx={{ py: 1.25, px: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.11)', mb: 1.5 }}>
              {[
                ['Invoice amount', `$${invoiceAmt.toFixed(2)}`],
                ['Processing fee', `+$${computeT2AFee(invoiceAmt, t2aRail).fee.toFixed(2)}`],
              ].map(([label, val]) => (
                <Stack key={label} direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 11 }}>{label}</Typography>
                  <Typography variant="caption" fontWeight={600} sx={{ color: 'white', fontSize: 11 }}>{val}</Typography>
                </Stack>
              ))}
              <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.18)', my: 0.25 }} />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>You pay</Typography>
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'white' }}>
                  ${computeT2AFee(invoiceAmt, t2aRail).senderPays.toFixed(2)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>
                  {invoice?.creator_id || invoice?.client_name || 'Recipient'} receives
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>
                  ${invoiceAmt.toFixed(2)}
                </Typography>
              </Stack>
            </Stack>

            <Stack spacing={0.75}>
              {guidState === 'loading' && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <CircularProgress size={10} sx={{ color: 'rgba(255,255,255,0.6)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 10.5 }}>
                    Verifying recipient…
                  </Typography>
                </Stack>
              )}
              {guidState === 'ok' && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <LockIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 10.5 }}>
                    Payment credits {invoice?.creator_id || invoice?.client_name || 'recipient'}'s Blitz balance
                  </Typography>
                </Stack>
              )}
              {guidState === 'fail' && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <WarnIcon sx={{ fontSize: 11, color: 'rgba(255,210,80,0.9)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,210,80,0.9)', fontSize: 10.5 }}>
                    Recipient lookup pending — payment may still proceed
                  </Typography>
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <RedirectIcon sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10.5 }}>
                  You'll be redirected to {t2aRail === 'paypal' ? 'PayPal' : 'secure card checkout'} to complete payment
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // ─── Spending Power card ──────────────────────────────────────────────────
  const SpendCard = () => {
    const isSelected = payMethod === 'credit';
    return (
      <Box onClick={() => setPayMethod('credit')}
        sx={{
          p: isMobile ? 2 : 2.5, borderRadius: isMobile ? 2.5 : 3,
          border: '2px solid',
          borderColor: isSelected ? 'transparent' : alpha(theme.palette.grey[300], 0.5),
          background: isSelected ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : alpha(theme.palette.grey[100], 0.5),
          cursor: canSpend ? 'pointer' : 'not-allowed',
          opacity: canSpend ? 1 : 0.45,
          transition: 'all 0.25s ease',
          transform: isSelected ? 'scale(1.012)' : 'scale(1)',
          boxShadow: isSelected ? '0 12px 28px rgba(102,126,234,0.3)' : '0 2px 8px rgba(0,0,0,0.05)',
        }}>
        <Stack spacing={1.75}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2,
              bgcolor: isSelected ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.primary.main, 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCardIcon sx={{ fontSize: 20, color: isSelected ? 'white' : theme.palette.primary.main }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.2 }}>
                <Typography variant="subtitle2" fontWeight={700}
                  sx={{ color: isSelected ? 'white' : 'text.primary', fontSize: '0.95rem' }}>
                  Spending Power
                </Typography>
                <Chip label="5% Fee" size="small" sx={{
                  height: 17, fontSize: 10, fontWeight: 700,
                  bgcolor: isSelected ? 'rgba(255,255,255,0.22)' : alpha(theme.palette.warning.main, 0.1),
                  color: isSelected ? 'white' : theme.palette.warning.dark,
                }} />
                {isSelected && <CheckCircleIcon sx={{ fontSize: 14, color: 'white', ml: 'auto' }} />}
              </Stack>
              <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.78)' : 'text.secondary', fontSize: 11 }}>
                Pay now, settle at month-end
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center"
            sx={{ py: 1.25, px: 1.5, borderRadius: 2,
              bgcolor: isSelected ? 'rgba(255,255,255,0.13)' : alpha(theme.palette.grey[100], 0.8) }}>
            <Box>
              <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.62)' : 'text.secondary', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Available</Typography>
              <Typography variant="subtitle1" fontWeight={700}
                sx={{ color: isSelected ? 'white' : (canSpend ? theme.palette.success.main : theme.palette.error.main), fontSize: '1.05rem' }}>
                ${spendAvail.toFixed(2)}
              </Typography>
            </Box>
            <TrendingFlatIcon sx={{ color: isSelected ? 'rgba(255,255,255,0.4)' : 'text.disabled', fontSize: 18 }} />
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.62)' : 'text.secondary', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>After</Typography>
              <Typography variant="subtitle1" fontWeight={700}
                sx={{ color: isSelected ? 'white' : 'text.primary', fontSize: '1.05rem' }}>
                ${(spendAvail - totalCredit).toFixed(2)}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={0.65} sx={{ py: 1.25, px: 1.5, borderRadius: 2,
            bgcolor: isSelected ? 'rgba(255,255,255,0.1)' : alpha(theme.palette.warning.main, 0.07),
            border: '1px solid', borderColor: isSelected ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.warning.main, 0.2) }}>
            {[
              ['Invoice amount', `$${invoiceAmt.toFixed(2)}`],
              ['Platform fee (5%)', `+$${spendFee.toFixed(2)}`],
            ].map(([l, v]) => (
              <Stack key={l} direction="row" justifyContent="space-between">
                <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary', fontSize: 11 }}>{l}</Typography>
                <Typography variant="caption" fontWeight={600} sx={{ color: isSelected ? 'white' : 'text.primary', fontSize: 11 }}>{v}</Typography>
              </Stack>
            ))}
            <Box sx={{ height: 1, bgcolor: isSelected ? 'rgba(255,255,255,0.2)' : 'divider', my: 0.25 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={700} sx={{ color: isSelected ? 'white' : 'text.primary' }}>Total</Typography>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: isSelected ? 'white' : theme.palette.warning.dark }}>${totalCredit.toFixed(2)}</Typography>
            </Stack>
          </Stack>

          {!canSpend && (
            <Box sx={{ py: 0.75, px: 1.25, borderRadius: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.08), border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.25) }}>
              <Typography variant="caption" sx={{ color: theme.palette.error.dark, fontSize: 11, fontWeight: 600 }}>
                Insufficient spending power · Need ${Math.max(0, totalCredit - spendAvail).toFixed(2)} more
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  // ─── Action button ────────────────────────────────────────────────────────
  const ActionBtn = () => {
    const isT2A = payMethod === 'balance' && t2aOpen;
    return (
      <Button onClick={handlePay} variant="contained"
        disabled={!canProceed || isBusy || (isT2A && guidState === 'loading')}
        size="large" fullWidth
        endIcon={isBusy
          ? <CircularProgress size={16} sx={{ color: 'white' }} />
          : isT2A ? <RedirectIcon sx={{ fontSize: 16 }} /> : <ArrowIcon />}
        sx={{
          borderRadius: 2, height: isMobile ? 54 : 52, fontWeight: 700, fontSize: 15,
          background: isBusy || !canProceed ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: !isBusy && canProceed ? '0 4px 14px rgba(102,126,234,0.4)' : 'none',
          '&:hover': { transform: !isBusy && canProceed ? 'translateY(-1px)' : 'none' },
          transition: 'all 0.2s ease',
          '&:disabled': { background: alpha(theme.palette.grey[400], 0.5), boxShadow: 'none' },
        }}>
        {btnLabel}
      </Button>
    );
  };

  const AmountDisplay = () => (
    <Box sx={{ textAlign: 'center', py: isMobile ? 2.5 : 3, px: 2,
      borderRadius: 3, bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
        Invoice Amount
      </Typography>
      <Typography variant="h2" fontWeight={800} sx={{ mt: 1, fontSize: isMobile ? '2.2rem' : '2.5rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        ${invoiceAmt.toFixed(2)}
      </Typography>
      {invoice?.notes && (
        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.5 }}>
          {invoice.notes}
        </Typography>
      )}
    </Box>
  );

  const InfoBox = () => {
    const isT2A = payMethod === 'balance' && t2aOpen;
    return (
      <Box sx={{ py: 1.6, px: 2.25, borderRadius: 2,
        bgcolor: alpha(theme.palette.info.main, 0.07), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.18) }}>
        <Typography variant="caption" sx={{ color: theme.palette.info.dark, fontSize: 11.5, lineHeight: 1.6, display: 'block' }}>
          {isT2A
            ? `✓ You'll be redirected to ${t2aRail === 'paypal' ? 'PayPal' : 'card checkout'} · Recipient's Blitz balance is credited on payment confirmation`
            : payMethod === 'credit'
            ? "✓ Vendor receives payment immediately · You'll be billed at month-end"
            : '✓ Funds transferred instantly with no additional charges'}
        </Typography>
      </Box>
    );
  };

  // ─── Mobile ───────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Dialog open={open} onClose={onClose} fullScreen
        PaperProps={{ sx: { borderRadius: 0 } }}
        TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
        <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          px: 3, pt: `calc(18px + ${safeTop})`, pb: 3, position: 'relative', overflow: 'hidden',
          '&::before': { content: '""', position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)' } }}>
          <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h5" fontWeight={800} sx={{ color: 'white', fontSize: '1.4rem' }}>Pay Invoice</Typography>
              <IconButton onClick={onClose} size="small"
                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip label={`#${invoice?.po_number}`} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: 11, height: 24 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.88rem' }}>
                {invoice?.client_name || invoice?.creator_name}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', pb: `calc(16px + ${safeBottom} + ${appShellHeight}px)`,
          bgcolor: alpha(theme.palette.grey[50], 1), WebkitOverflowScrolling: 'touch' }}>
          <Box sx={{ px: 3, py: 3 }}>
            <Stack spacing={2.5}>
              <AmountDisplay />
              <Typography variant="subtitle2" fontWeight={700}
                sx={{ color: 'text.secondary', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Payment Method
              </Typography>
              <BalanceCard />
              <SpendCard />
              <InfoBox />
            </Stack>
          </Box>
        </Box>

        <Box sx={{ position: 'fixed', bottom: `${appShellHeight}px`, left: 0, right: 0,
          px: 3, pb: `calc(14px + ${safeBottom})`, pt: 2,
          bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.05)', zIndex: 1 }}>
          <Stack spacing={1.25}>
            <ActionBtn />
            <Button onClick={onClose} disabled={isBusy} variant="outlined" size="large" fullWidth
              sx={{ borderRadius: 2, height: 46, fontWeight: 600, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>
              Cancel
            </Button>
          </Stack>
        </Box>
      </Dialog>
    );
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
      TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        px: 4, py: 3, position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)' } }}>
        <Stack spacing={0.75} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" fontWeight={800} sx={{ color: 'white' }}>Pay Invoice</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label={`#${invoice?.po_number}`} size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: 11, height: 22 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {invoice?.client_name || invoice?.creator_name}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 4, bgcolor: alpha(theme.palette.grey[50], 1) }}>
        <Stack spacing={3}>
          <AmountDisplay />
          <Typography variant="subtitle2" fontWeight={700}
            sx={{ color: 'text.secondary', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Payment Method
          </Typography>
          <BalanceCard />
          <SpendCard />
          <InfoBox />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 4, pt: 2, bgcolor: alpha(theme.palette.grey[50], 1), gap: 2 }}>
        <Button onClick={onClose} disabled={isBusy} variant="outlined" size="large" fullWidth
          sx={{ borderRadius: 2, height: 52, fontWeight: 600, borderWidth: 2, '&:hover': { borderWidth: 2 } }}>
          Cancel
        </Button>
        <ActionBtn />
      </DialogActions>
    </Dialog>
  );
}